'use client';
// ──────────────────────────────────────────────────────
// InactivityProvider — redirects to idle after 90s
// ──────────────────────────────────────────────────────
import { useEffect, useRef, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { INACTIVITY_TIMEOUT_MS } from '@/lib/tokens';

export function InactivityProvider({ children }: { children: React.ReactNode }) {
    const router = useRouter();
    const pathname = usePathname();
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const resetTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        // Only set timer on kiosk screens — never on admin or mobile routes
        if (
            pathname !== '/' &&
            !pathname.startsWith('/admin') &&
            !pathname.startsWith('/m/')
        ) {
            timerRef.current = setTimeout(() => {
                router.push('/');
            }, INACTIVITY_TIMEOUT_MS);
        }
    }, [pathname, router]);

    useEffect(() => {
        resetTimer();

        const events = ['touchstart', 'mousedown', 'keydown', 'scroll'] as const;
        const handler = () => resetTimer();

        events.forEach((event) => window.addEventListener(event, handler, { passive: true }));

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            events.forEach((event) => window.removeEventListener(event, handler));
        };
    }, [resetTimer]);

    return <>{children}</>;
}
